import { useQuery } from '@tanstack/react-query';
import { getBookingById } from '@/backend/services/bookings';
import type { GuestInfo } from '@/shared/types';

export interface MyGuestDetails {
  bookingId: string;
  /** The caller's own entry in the booking's guests array. */
  guest: GuestInfo;
}

/**
 * The signed-in student's own guest entry on their booking.
 *
 * Used by the portal guard to decide whether to ask them to complete the
 * personal details that bookings created before the DEV2-1038 write-path fix
 * never stored. Resolution mirrors the Cloud Function: `studentIds` is
 * index-aligned with `guests`, with an email match as the fallback for bookings
 * approved before that link existed.
 */
export const useMyGuestDetails = (
  studentId: string | undefined,
  bookingId: string | undefined,
  email: string | undefined
) => {
  return useQuery({
    queryKey: ['my-guest-details', studentId, bookingId],
    enabled: !!studentId && !!bookingId,
    queryFn: async (): Promise<MyGuestDetails | null> => {
      const booking = await getBookingById(bookingId!);
      if (!booking?.guests?.length) return null;

      let index = booking.studentIds?.indexOf(studentId!) ?? -1;
      if (index === -1 && email) {
        index = booking.guests.findIndex(
          (g) => g.email?.toLowerCase() === email.toLowerCase()
        );
      }
      if (index === -1) return null;

      return { bookingId: booking.id, guest: booking.guests[index] };
    },
  });
};
